// frontend/src/locales/pricing.ts
/**
 * Pricing page translations (English, Turkish, Spanish, German, Chinese, Japanese, Korean)
 * Updated according to SaaS Commercial Packages Specification (No prices displayed, contact sales CTAs)
 */

export const pricingTranslations = {
    en: {
        title: 'Professional Lashing Intelligence',
        subtitle: 'Choose the commercial package tailored to your logistics operations',
        currency: '£',
        contactForPricing: 'Contact for pricing',
        billingToggle: {
            monthly: 'Monthly',
            annual: 'Annual',
            save: 'Save with Annual Billing'
        },
        duration: {
            month: 'month',
            year: 'year',
            mo: 'mo'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: 'Contact Sales',
                priceSubtext: 'Billed annually or monthly',
                description: 'Perfect for local hauliers, small securing teams, and regional sites requiring standard compliance.',
                features: [
                    '1 Authorized User Seat (Extra seats available)',
                    '50 PDF Reports / year allotment',
                    '3 Year Secure Archive History',
                    '5 GB Cloud Storage (~1,000 photo reports)',
                    'Access to all transport modes (Road, Rail, Sea)',
                    'Standard transport units (Containers & Trailers)',
                    'Standard Friction Coefficient Database',
                    'Tamper-proof timestamped PDF exports',
                    'Additional report options available',
                    'Email support (24hr SLA)'
                ],
                cta: 'Request Quote'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: 'Contact Sales',
                priceSubtext: 'Billed annually or monthly',
                description: 'Designed for dedicated cargo securing companies, active marine surveyors, and regional hubs.',
                features: [
                    'Up to 3 Authorized User Seats (Extra seats available)',
                    '150 PDF Reports / year allotment',
                    '3 Year Secure Archive History',
                    '25 GB Cloud Storage (~10,000 photo reports)',
                    'All transport modes & ALL transport units (Flatrack, Mafi, Vessel)',
                    'Real-time 3D Center of Gravity & Lashing Twin',
                    'Custom Company Logo on PDF Reports',
                    'Standard Friction Coefficient Database',
                    'Tamper-proof timestamped PDF exports',
                    'Additional report options available',
                    'Priority Email support (24hr SLA)'
                ],
                cta: 'Request Quote',
                badge: 'Recommended'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: 'Custom Quote',
                priceSubtext: 'Billed annually',
                description: 'Tailored for port terminal operators, global surveyor firms, shipping lines, and underwriters.',
                features: [
                    'Custom / Unlimited User Seats',
                    'Custom / Unlimited PDF Reports & Calculations',
                    '3 Year Secure Archive & Unlimited Cloud Storage',
                    'All transport modes & ALL transport units',
                    'Real-time 3D Center of Gravity & Lashing Twin',
                    'Custom Company Logo on PDF Reports',
                    'SSO & Corporate Fleet Management',
                    'API Integration Options (ERP/TMS)',
                    'Dedicated Account Manager & Priority SLA'
                ],
                cta: 'Contact Sales'
            }
        },
        faq: {
            title: 'Frequently Asked Questions',
            items: [
                {
                    question: 'Can I add extra user seats to my plan?',
                    answer: 'Yes, additional user seats can be added to both Starter and Professional plans at any time.'
                },
                {
                    question: 'What happens if we exceed our annual report allotment?',
                    answer: 'Additional PDF reports can easily be added to your account on a flexible pay-as-you-go basis without interrupting operations.'
                },
                {
                    question: 'Are PDF calculation reports accepted by authorities?',
                    answer: 'Acceptance depends on the authority, carrier, jurisdiction, and transport mode. Reports are decision-support documents and require review by the relevant qualified professional and carrier.'
                },
                {
                    question: 'Do you support custom company branding?',
                    answer: 'Professional and Enterprise plans allow you to add your corporate logo to PDF calculation reports.'
                }
            ]
        },
        trust: {
            title: 'Trusted by Cargo Securing Professionals',
            stats: [
                { value: '1000+', label: 'Calculations Performed' },
                { value: '24/7', label: 'Global Access' },
                { value: 'Secure', label: 'Cloud Storage & Archive' }
            ]
        },
        nav: {
            back: 'Back',
            signIn: 'Sign In'
        },
        footerCTA: {
            title: 'Need a Custom Solution?',
            subtitle: 'Get in touch with our engineering team to discuss custom integrations or high-volume licensing.',
            cta: 'Contact Sales'
        },
        loading: 'Loading...'
    },
    tr: {
        title: 'Profesyonel Lashing Analizi',
        subtitle: 'Lojistik operasyonlarınıza uygun ticari paketi seçin',
        currency: '£',
        contactForPricing: 'Fiyat için iletişime geçin',
        billingToggle: {
            monthly: 'Aylık',
            annual: 'Yıllık',
            save: 'Yıllık Ödemede Tasarruf Et'
        },
        duration: {
            month: 'ay',
            year: 'yıl',
            mo: 'ay'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: 'Teklif Alın',
                priceSubtext: 'Yıllık veya aylık faturalandırma',
                description: 'Yerel nakliyeciler, küçük lashing ekipleri ve standart uyumluluk isteyen bölgesel sahalar için ideal.',
                features: [
                    '1 Yetkili Kullanıcı Koltuğu (Ekstra koltuk eklenebilir)',
                    'Yıllık 50 PDF Raporu Kontenjanı',
                    '3 Yıllık Güvenli Arşiv Geçmişi',
                    '5 GB Bulut Depolama (~1.000 fotoğraflı rapor)',
                    'Tüm taşıma modları (Kara, Demiryolu, Deniz)',
                    'Standart Taşıma Üniteleri (Konteyner ve Tır Dorsesi)',
                    'Standart Sürtünme Katsayısı Veritabanı',
                    'Zaman damgalı korumalı PDF rapor çıktısı',
                    'Esnek ek rapor seçenekleri',
                    '24 Saat SLA E-posta Desteği'
                ],
                cta: 'Teklif Alın'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: 'Teklif Alın',
                priceSubtext: 'Yıllık veya aylık faturalandırma',
                description: 'Lashing şirketleri, aktif deniz sörveyörleri ve bölgesel lojistik merkezleri için tasarlandı.',
                features: [
                    '3 Yetkili Kullanıcıya Kadar (Ekstra koltuk eklenebilir)',
                    'Yıllık 150 PDF Raporu Kontenjanı',
                    '3 Yıllık Güvenli Arşiv Geçmişi',
                    '25 GB Bulut Depolama (~10.000 fotoğraflı rapor)',
                    'Tüm Taşıma Modları VE Tüm Üniteler (Flatrack, Mafi, Gemi)',
                    'Gerçek Zamanlı 3D Ağırlık Merkezi ve Lashing Görselleştirme',
                    'PDF Raporlarında Kurumsal Şirket Logosu',
                    'Standart Sürtünme Katsayısı Veritabanı',
                    'Zaman damgalı korumalı PDF rapor çıktısı',
                    'Esnek ek rapor seçenekleri',
                    '24 Saat SLA Öncelikli E-posta Desteği'
                ],
                cta: 'Teklif Alın',
                badge: 'Önerilen'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: 'Özel Fiyat',
                priceSubtext: 'Yıllık faturalandırma',
                description: 'Liman terminal operatörleri, küresel sörveyör firmaları ve armatörler için özel çözümler.',
                features: [
                    'Özel / Sınırsız Kullanıcı Koltuğu',
                    'Özel / Sınırsız PDF Raporu ve Hesaplama',
                    '3 Yıl Arşiv ve Sınırsız Bulut Depolama',
                    'Tüm Taşıma Modları VE Tüm Taşıma Üniteleri',
                    'Gerçek Zamanlı 3D Ağırlık Merkezi ve Lashing Görselleştirme',
                    'PDF Raporlarında Kurumsal Şirket Logosu',
                    'SSO ve Kurumsal Filo Yönetimi',
                    'API Entegrasyon Seçenekleri (ERP/TMS)',
                    'Özel Müşteri Yöneticisi ve Öncelikli SLA'
                ],
                cta: 'Satışla İletişime Geçin'
            }
        },
        faq: {
            title: 'Sıkça Sorulan Sorular',
            items: [
                {
                    question: 'Plana ekstra kullanıcı ekleyebilir miyiz?',
                    answer: 'Evet, hem Starter hem de Professional planlarına istediğiniz zaman ek kullanıcı koltukları ekleyebilirsiniz.'
                },
                {
                    question: 'Yıllık rapor kotamız dolarsa ne olur?',
                    answer: 'Operasyonlarınız aksamadan hesabınıza esnek ek rapor paketi tanımlanabilir.'
                },
                {
                    question: 'PDF hesaplama raporları resmî kurumlarca kabul edilir mi?',
                    answer: 'Kabul; kuruma, taşıyıcıya, yetki alanına ve taşıma moduna göre değişir. Raporlar karar destek dokümanıdır ve ilgili yetkili uzman ile taşıyıcının incelemesini gerektirir.'
                },
                {
                    question: 'Kendi şirket logomuzu raporlara ekleyebilir miyiz?',
                    answer: 'Professional ve Enterprise planlarında kurumsal logonuzu yükleyerek raporları markalayabilirsiniz.'
                }
            ]
        },
        trust: {
            title: 'Lojistik Profesyonellerinin Güvendiği',
            stats: [
                { value: '1000+', label: 'Gerçekleştirilen Hesaplama' },
                { value: '7/24', label: 'Küresel Erişim' },
                { value: 'Güvenli', label: 'Bulut Depolama ve Arşiv' }
            ]
        },
        nav: {
            back: 'Geri',
            signIn: 'Giriş Yap'
        },
        footerCTA: {
            title: 'Özel Bir Çözüme mi İhtiyacınız Var?',
            subtitle: 'Özel entegrasyonlar veya yüksek hacimli lisanslama için mühendislik ekibimizle iletişime geçin.',
            cta: 'Satışla İletişime Geçin'
        },
        loading: 'Yükleniyor...'
    },
    es: {
        title: 'Inteligencia de Trincaje Profesional',
        subtitle: 'Elija el paquete comercial adaptado a sus operaciones logísticas',
        currency: '£',
        contactForPricing: 'Consultar precio',
        billingToggle: {
            monthly: 'Mensual',
            annual: 'Anual',
            save: 'Ahorra con facturación anual'
        },
        duration: {
            month: 'mes',
            year: 'año',
            mo: 'mes'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: 'Solicitar cotización',
                priceSubtext: 'Facturación anual o mensual',
                description: 'Perfecto para pequeñas operaciones y equipos de trincaje locales.',
                features: [
                    '1 asiento de usuario autorizado',
                    'Asignación de 50 informes PDF / año',
                    '3 años de historial de archivo seguro',
                    '5 GB de almacenamiento en la nube',
                    'Todos los modos de transporte (Carretera, Ferrocarril, Mar)',
                    'Unidades de transporte estándar (Contenedores y Remolques)',
                    'Base de datos de coeficientes de fricción',
                    'Informes PDF con marca de tiempo segura',
                    'Soporte por correo electrónico (SLA 24h)'
                ],
                cta: 'Solicitar cotización'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: 'Solicitar cotización',
                priceSubtext: 'Facturación anual o mensual',
                description: 'Para empresas de trincaje dedicadas, peritos marinos y centros regionales.',
                features: [
                    'Hasta 3 asientos de usuario autorizados',
                    'Asignación de 150 informes PDF / año',
                    '3 años de historial de archivo seguro',
                    '25 GB de almacenamiento en la nube',
                    'Todos los modos y TODAS las unidades de transporte (Flatrack, Mafi, Buque)',
                    'Visualización 3D en tiempo real (Centro de gravedad y trincaje)',
                    'Logo corporativo personalizado en informes PDF',
                    'Soporte por correo electrónico prioritario (SLA 24h)'
                ],
                cta: 'Solicitar cotización',
                badge: 'Recomendado'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: 'Cotización personalizada',
                priceSubtext: 'Facturación anual',
                description: 'Soluciones a medida para operadores de terminales, armadores y aseguradoras.',
                features: [
                    'Asientos de usuario personalizados / ilimitados',
                    'Informes PDF y cálculos personalizados / ilimitados',
                    'Almacenamiento en la nube ilimitado y 3 años de archivo',
                    'Visualización 3D y marcas personalizadas',
                    'SSO y gestión de flota corporativa',
                    'Opciones de integración API (ERP/TMS)',
                    'Gestor de cuenta dedicado y SLA prioritario'
                ],
                cta: 'Contactar Ventas'
            }
        },
        faq: {
            title: 'Preguntas Frecuentes',
            items: [
                {
                    question: '¿Puedo añadir asientos de usuario adicionales?',
                    answer: 'Sí, se pueden añadir asientos de usuario adicionales en cualquier momento.'
                }
            ]
        },
        trust: {
            title: 'Confiado por Profesionales del Transporte',
            stats: [
                { value: '1000+', label: 'Cálculos Realizados' },
                { value: '24/7', label: 'Acceso Global' },
                { value: 'Seguro', label: 'Plataforma en la Nube' }
            ]
        },
        nav: {
            back: 'Volver',
            signIn: 'Iniciar sesión'
        },
        footerCTA: {
            title: '¿Necesita una solución personalizada?',
            subtitle: 'Póngase en contacto con nuestro equipo para integraciones personalizadas.',
            cta: 'Contactar Ventas'
        },
        loading: 'Cargando...'
    },
    de: {
        title: 'Professionelle Zurrintelligenz',
        subtitle: 'Wählen Sie das kommerzielle Paket für Ihre Logistikabläufe',
        currency: '£',
        contactForPricing: 'Preis auf Anfrage',
        billingToggle: {
            monthly: 'Monatlich',
            annual: 'Jährlich',
            save: 'Sparen bei jährlicher Zahlung'
        },
        duration: {
            month: 'Monat',
            year: 'Jahr',
            mo: 'Mon'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: 'Angebot anfordern',
                priceSubtext: 'Jährlich oder monatlich abrechenbar',
                description: 'Ideal für kleine Betriebe und regionale Logistikstandorte.',
                features: [
                    '1 autorisierter Benutzerplatz',
                    '50 PDF-Berichte / Jahr Kontingent',
                    '3 Jahre sichere Archivhistorie',
                    '5 GB Cloud-Speicherplatz',
                    'Alle Transportmodi (Straße, Schiene, See)',
                    'Standard-Transporteinheiten (Container & Trailer)',
                    'Reibungsbeiwert-Datenbank',
                    'Zeitgestempelte PDF-Exporte',
                    'E-Mail-Support (24h SLA)'
                ],
                cta: 'Angebot anfordern'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: 'Angebot anfordern',
                priceSubtext: 'Jährlich oder monatlich abrechenbar',
                description: 'Für spezialisierte Zurrunternehmen und Schiffsbesichtiger.',
                features: [
                    'Bis zu 3 autorisierte Benutzerplätze',
                    '150 PDF-Berichte / Jahr Kontingent',
                    '3 Jahre sichere Archivhistorie',
                    '25 GB Cloud-Speicherplatz',
                    'Alle Transportmodi & ALLE Einheiten (Flatrack, Mafi, Schiff)',
                    'Echtzeit-3D-Visualisierung (Schwerpunkt & Zurrung)',
                    'Individuelles Firmenlogo auf PDF-Berichten',
                    'Priorisierter E-Mail-Support (24h SLA)'
                ],
                cta: 'Angebot anfordern',
                badge: 'Empfohlen'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: 'Individuelles Angebot',
                priceSubtext: 'Jährlich abrechenbar',
                description: 'Maßgeschneidert für Terminalbetreiber, Reedereien und Versicherer.',
                features: [
                    'Individuelle / Unbegrenzte Benutzerplätze',
                    'Individuelle / Unbegrenzte PDF-Berichte & Berechnungen',
                    'Unbegrenzter Cloud-Speicher & 3 Jahre Archiv',
                    'Echtzeit-3D-Visualisierung & Branding',
                    'SSO & Corporate-Flottenmanagement',
                    'API-Integrationsoptionen (ERP/TMS)',
                    'Dedicated Account Manager & Prioritäts-SLA'
                ],
                cta: 'Vertrieb kontaktieren'
            }
        },
        faq: {
            title: 'Häufig gestellte Fragen',
            items: [
                {
                    question: 'Kann ich zusätzliche Benutzerplätze hinzufügen?',
                    answer: 'Ja, zusätzliche Benutzerplätze können jederzeit hinzugefügt werden.'
                }
            ]
        },
        trust: {
            title: 'Von Transportprofis geschätzt',
            stats: [
                { value: '1000+', label: 'Berechnungen durchgeführt' },
                { value: '24/7', label: 'Globaler Zugriff' },
                { value: 'Sicher', label: 'Cloud-Plattform' }
            ]
        },
        nav: {
            back: 'Zurück',
            signIn: 'Anmelden'
        },
        footerCTA: {
            title: 'Benötigen Sie eine individuelle Lösung?',
            subtitle: 'Kontaktieren Sie unser Ingenieurteam für maßgeschneiderte Integrationen.',
            cta: 'Vertrieb kontaktieren'
        },
        loading: 'Laden...'
    },
    zh: {
        title: '专业系固智能',
        subtitle: '选择适合您物流运营的商业套餐',
        currency: '£',
        contactForPricing: '联系获取报价',
        billingToggle: {
            monthly: '按月',
            annual: '按年',
            save: '按年结算更优惠'
        },
        duration: {
            month: '月',
            year: '年',
            mo: '月'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: '请求报价',
                priceSubtext: '按年或按月结算',
                description: '适合小型运营团队和区域物流站点。',
                features: [
                    '1 个授权用户席位',
                    '每年 50 份 PDF 报告配额',
                    '3 年安全存档历史',
                    '5 GB 云端存储空间',
                    '支持所有运输模式（公路、铁路、海运）',
                    '标准运输单元（集装箱和拖车）',
                    '防篡改时间戳 PDF 导出',
                    '电子邮件支持（24小时响应）'
                ],
                cta: '请求报价'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: '请求报价',
                priceSubtext: '按年或按月结算',
                description: '专为货劳检验员和专业系固公司设计。',
                features: [
                    '最多 3 个授权用户席位',
                    '每年 150 份 PDF 报告配额',
                    '3 年安全存档历史',
                    '25 GB 云端存储空间',
                    '所有运输模式及所有单元（框架箱、马菲板、船舶）',
                    '实时 3D 重心与系固孪生展示',
                    'PDF 报告自定义公司 Logo',
                    '优先电子邮件支持（24小时响应）'
                ],
                cta: '请求报价',
                badge: '推荐'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: '定制报价',
                priceSubtext: '按年结算',
                description: '为码头运营商、船公司和保险公司量身定制。',
                features: [
                    '定制 / 无限用户席位',
                    '定制 / 无限 PDF 报告与计算',
                    '3 年存档及无限云存储',
                    'SSO 单点登录与企业舰队管理',
                    'API 集成选项（ERP/TMS）',
                    '专属客户经理与优先服务协议'
                ],
                cta: '联系销售'
            }
        },
        faq: {
            title: '常见问题',
            items: [
                {
                    question: '我可以添加额外的用户席位吗？',
                    answer: '是的，您可以随时添加额外的用户席位。'
                }
            ]
        },
        trust: {
            title: '深受运输专业人士信赖',
            stats: [
                { value: '1000+', label: '已执行计算' },
                { value: '24/7', label: '全球访问' },
                { value: '安全', label: '云平台' }
            ]
        },
        nav: {
            back: '返回',
            signIn: '登录'
        },
        footerCTA: {
            title: '需要定制解决方案？',
            subtitle: '联系我们的工程团队讨论定制集成。',
            cta: '联系销售'
        },
        loading: '加载中...'
    },
    ja: {
        title: 'プロフェッショナル・ラッシング・インテリジェンス',
        subtitle: '物流オペレーションに最適な商用パッケージをお選びください',
        currency: '£',
        contactForPricing: '価格についてはお問い合わせください',
        billingToggle: {
            monthly: '月払い',
            annual: '年払い',
            save: '年払いで割引'
        },
        duration: {
            month: '月',
            year: '年',
            mo: '月'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: '見積もりをリクエスト',
                priceSubtext: '年払いまたは月払い',
                description: '小規模な運用や地域の物流拠点に最適です。',
                features: [
                    '1 認定ユーザーシート',
                    '年間 50 件の PDF レポート割当',
                    '3 年間の安全なアーカイブ履歴',
                    '5 GB のクラウドストレージ',
                    'すべての輸送モード（道路、鉄道、海上）',
                    '標準輸送ユニット（コンテナ＆トレーラー）',
                    'タイムスタンプ付き PDF 出力',
                    'メールサポート（24時間SLA）'
                ],
                cta: '見積もりをリクエスト'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: '見積もりをリクエスト',
                priceSubtext: '年払いまたは月払い',
                description: '専門のラッシング会社や海事検数人向けに設計されています。',
                features: [
                    '最大 3 認定ユーザーシート',
                    '年間 150 件の PDF レポート割当',
                    '3 年間の安全なアーカイブ履歴',
                    '25 GB のクラウドストレージ',
                    'すべての輸送モード＆全ユニット（フラットラック、マフィ、本船）',
                    'リアルタイム 3D 重心＆ラッシングツイン表示',
                    'PDF レポートへのカスタム会社ロゴ掲載',
                    '優先メールサポート（24時間SLA）'
                ],
                cta: '見積もりをリクエスト',
                badge: 'おすすめ'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: 'カスタム見積もり',
                priceSubtext: '年払い',
                description: 'ターミナルオペレーターや船会社向けにカスタマイズ。',
                features: [
                    'カスタム / 無制限ユーザーシート',
                    'カスタム / 無制限 PDF レポート＆計算',
                    '3 年間アーカイブ＆無制限クラウドストレージ',
                    'SSO ＆ 企業フリート管理',
                    'API 統合オプション（ERP/TMS）',
                    '専任アカウントマネージャー＆優先SLA'
                ],
                cta: '営業に問い合わせ'
            }
        },
        faq: {
            title: 'よくある質問',
            items: [
                {
                    question: '追加のユーザーシートを追加できますか？',
                    answer: 'はい、いつでも追加のユーザーシートを追加できます。'
                }
            ]
        },
        trust: {
            title: '輸送のプロフェッショナルから信頼されています',
            stats: [
                { value: '1000+', label: '実行された計算' },
                { value: '24/7', label: 'グローバルアクセス' },
                { value: '安全', label: 'クラウドプラットフォーム' }
            ]
        },
        nav: {
            back: '戻る',
            signIn: 'サインイン'
        },
        footerCTA: {
            title: 'カスタムソリューションが必要ですか？',
            subtitle: 'カスタム統合についてエンジニアリングチームにお問い合わせください。',
            cta: '営業に問い合わせ'
        },
        loading: '読み込み中...'
    },
    ko: {
        title: '전문 화물 라싱 인텔리전스',
        subtitle: '귀하의 물류 운영에 맞춘 상업용 패키지를 선택하세요',
        currency: '£',
        contactForPricing: '가격 문의',
        billingToggle: {
            monthly: '월간',
            annual: '연간',
            save: '연간 결제 시 할인'
        },
        duration: {
            month: '월',
            year: '년',
            mo: '월'
        },
        tiers: {
            basic: {
                name: 'Starter Plan',
                priceLabel: '견적 요청',
                priceSubtext: '연간 또는 월간 결제',
                description: '소규모 운송팀 및 지역 물류 현장에 적합합니다.',
                features: [
                    '1인 인증 사용자 계정 (추가 계정 가능)',
                    '연간 50건 PDF 보고서 할당량',
                    '3년 안전 아카이브 이력 보장',
                    '5 GB 보안 클라우드 저장소 (~1,000건 사진 보고서)',
                    '모든 운송 모드 지원 (도로, 철도, 해상)',
                    '표준 운송 단위 지원 (컨테이너 및 트럭 트레일러)',
                    '보안 타임스탬프 적용 PDF 보고서 출력',
                    '유연한 추가 보고서 옵션 제공',
                    '이메일 고객 지원 (24시간 SLA)'
                ],
                cta: '견적 요청'
            },
            premium: {
                name: 'Professional Plan',
                priceLabel: '견적 요청',
                priceSubtext: '연간 또는 월간 결제',
                description: '전문 화물 고정 업체 및 해사 검정법인을 위해 설계되었습니다.',
                features: [
                    '최대 3인 인증 사용자 계정 (추가 계정 가능)',
                    '연간 150건 PDF 보고서 할당량',
                    '3년 안전 아카이브 이력 보장',
                    '25 GB 보안 클라우드 저장소 (~10,000건 사진 보고서)',
                    '모든 운송 모드 및 모든 운송 단위 (플랫랙, 마피, 선박)',
                    '실시간 3D 무게 중심 및 라싱 시각화 모델',
                    'PDF 보고서 내 기업 전용 로고 반영 (Custom Branding)',
                    '보안 타임스탬프 적용 PDF 보고서 출력',
                    '유연한 추가 보고서 옵션 제공',
                    '우선순위 이메일 고객 지원 (24시간 SLA)'
                ],
                cta: '견적 요청',
                badge: '추천 패키지'
            },
            enterprise: {
                name: 'Large Enterprise',
                priceLabel: '맞춤 견적',
                priceSubtext: '연간 결제',
                description: '항만 터미널 운영사, 글로벌 검정법인 및 선사를 위한 전용 솔루션.',
                features: [
                    '맞춤형 / 무제한 사용자 계정',
                    '맞춤형 / 무제한 PDF 보고서 및 연산',
                    '3년 아카이브 및 무제한 클라우드 저장소',
                    '모든 운송 모드 및 모든 운송 단위 지원',
                    '실시간 3D 무게 중심 및 라싱 시각화 모델',
                    'PDF 보고서 내 기업 전용 로고 반영',
                    'SSO (단일 로그인) 및 기업 전용 관리 시스템',
                    'API 연동 옵션 (ERP/TMS)',
                    '전담 계정 매니저 및 우선순위 SLA'
                ],
                cta: '영업팀 문의'
            }
        },
        faq: {
            title: '자주 묻는 질문',
            items: [
                {
                    question: '사용자 계정을 추가로 구매할 수 있나요?',
                    answer: '네, Starter 및 Professional 플랜 모두 필요 시 사용자 계정을 추가하실 수 있습니다.'
                },
                {
                    question: '연간 보고서 할당량을 초과하면 어떻게 되나요?',
                    answer: '운영 중단 없이 용량에 맞춘 유연한 추가 보고서 옵션을 이용하실 수 있습니다.'
                }
            ]
        },
        trust: {
            title: '물류 전문가들이 신뢰하는 플랫폼',
            stats: [
                { value: '1000+', label: '누적 연산 수행' },
                { value: '24/7', label: '글로벌 접속' },
                { value: '보안', label: '클라우드 플랫폼' }
            ]
        },
        nav: {
            back: '뒤로',
            signIn: '로그인'
        },
        footerCTA: {
            title: '맞춤형 솔루션이 필요하신가요?',
            subtitle: '엔지니어링 팀과 직접 연락하여 맞춤형 API 연동 및 대량 라이선스를 논의해보세요.',
            cta: '영업팀 문의'
        },
        loading: '로딩 중...'
    }
};
